// ============================================================================
// ui/keyboard-inset.js — how tall is the on-screen keyboard, right now?
// ============================================================================
// ⛔ THE OBVIOUS ANSWER IS WRONG IN THIS APP, AND IT SHIPPED WRONG ONCE.
// "Watch `visualViewport.height`" is the standard recipe and it does nothing
// here, because WD 253 set `interactive-widget=overlays-content`. That keyword's
// whole purpose is that the keyboard changes NO viewport: not the layout one
// (which is what stopped the sheet, the canvas and the camera fit moving), and
// not the visual one either. So `visualViewport.height` stays at full height
// with the keyboard open, a panel sized from it does not move, and the composer
// sits behind the keyboard the user is typing into.
//
// ── THE THREE BEHAVIOURS, BECAUSE THE FIX DEPENDS ON WHICH ONE YOU ARE IN ────
//   overlays-content  (what this app declares) — nothing resizes. visualViewport
//                     is blind. Only the VirtualKeyboard API can see the keyboard.
//   resizes-visual    (the spec default, so most of the storefront) — the LAYOUT
//                     viewport holds still and the VISUAL one shrinks AND gains an
//                     `offsetTop`. A `position: fixed` box is laid out against the
//                     layout viewport, so it must be moved by `offsetTop` as well
//                     as shortened, or it ends up short of the keyboard with a gap
//                     underneath. (That is the storefront chat's bug, same day.)
//   resizes-content   — both viewports shrink; `100dvh` alone is already correct.
//
// So: ask the VirtualKeyboard API when it exists (Chromium, which is Android),
// and fall back to visualViewport (iOS Safari, which behaves like resizes-visual).
// Both paths report the same two numbers, and the caller never learns which one
// answered.
//
// ⚠ `overlaysContent = true` is set on the API because that is what makes
// `geometrychange` fire and `boundingRect` non-empty. It is not a behaviour
// change: the meta viewport already declares exactly this, so the keyboard was
// overlaying content either way. It only makes the geometry readable.
// ============================================================================

// ⛔ AND THE API IS SECURE-CONTEXT ONLY, WHICH IS WHY THE FIRST FIX LOOKED DEAD.
// `navigator.virtualKeyboard` is undefined on `http://10.0.0.127:5173` — the LAN dev
// server Ryan tests on is plain HTTP, so the only sensor that can see the keyboard
// under `overlays-content` is not exposed, the code fell through to a blind
// `visualViewport`, and the bug looked untouched. It would have worked in production
// and nowhere else, which is the worst possible place for a fix to work.
//   So the fallback stops being passive: it switches the page's `interactive-widget`
// to `resizes-visual` for as long as somebody is observing. That keyword shrinks the
// VISUAL viewport (which `visualViewport` can then report) while leaving the LAYOUT
// viewport alone — so `100dvh`, `.ds-canvas` and the camera's ResizeObserver still do
// not move, which is the entire property WD 253 bought with `overlays-content`.
// Restored on the last unsubscribe. One behaviour in dev and in production.

/** @typedef {{visibleHeight: number, offsetTop: number, keyboardHeight: number}} Inset */

/**
 * ⭐ IS THE KEYBOARD UP — AS AN EVENT, not as a height, and WITHOUT changing the viewport.
 *
 * `observeKeyboard` below answers "how much room is left" and pays for the answer by putting the
 * page on `resizes-content` — safe only for a surface that covers the canvas. The design sheet is
 * not that surface: WD 253 moved this app to `overlays-content` precisely so the keyboard could not
 * drag the sheet, the canvas and the camera fit around, and that must hold while a buyer types into
 * the Add Logo fields. So this reads the SAME sensors and reports one boolean, resizing nothing.
 *
 * ⛔ AND THE BOOLEAN IS THE POINT, because the app currently GUESSES it twice. Focus starts a 400ms
 * timer and hopes the keyboard has arrived; a blur is treated as the keyboard leaving. Both are
 * wrong on a real device — a cold IME takes longer than 400ms to animate up, and Android's Back
 * gesture dismisses the keyboard WITHOUT blurring the field, so nothing fires at all. Ryan
 * 2026-08-10: *"the keyboard closes but the window/slide-up menu/text fields don't go back to their
 * proper positions… the trigger should actually be when the keyboard retracts/appears, not when I
 * click outside of the field."*
 *
 * Two sensors, in order:
 *   1. `navigator.virtualKeyboard` — Chromium, i.e. Android, and the only thing that can see a
 *      keyboard under `overlays-content`. `overlaysContent = true` is what makes `geometrychange`
 *      fire at all; it is NOT a behaviour change (the meta already declares exactly this) and it is
 *      restored on unsubscribe so this can never disagree with `observeKeyboard`.
 *   2. `visualViewport` — iOS Safari, which does not implement `interactive-widget` and therefore
 *      still behaves like `resizes-visual`, so its height really does drop when the keyboard opens.
 *      The baseline is captured at SUBSCRIBE time, which is a focus event: the keyboard is by
 *      definition not up yet, so it is a true closed-state reference.
 *
 * ⚠ NEITHER ANSWERS ON A PLAIN-HTTP LAN DEV SERVER. `navigator.virtualKeyboard` is secure-context
 * only (the long note above), and `visualViewport` is blinded by our own `overlays-content`. The
 * caller must therefore keep a timer fallback rather than treating silence as "no keyboard" — that
 * is exactly how the first fix in this file came to work in production and nowhere else.
 *
 * @param {(state: {open: boolean}) => void} onChange fired only on a real transition
 * @returns {{stop: () => void, live: boolean}} `live` is false when no sensor answered
 */
export function watchKeyboardOpen(onChange) {
  if (typeof window === 'undefined') return { stop: () => {}, live: false };
  let open = null;
  const emit = (next) => { if (next === open) return; open = next; onChange({ open: next }); };

  const vk = typeof navigator !== 'undefined' ? navigator.virtualKeyboard : null;
  if (vk && typeof vk.addEventListener === 'function') {
    const wasOverlaying = vk.overlaysContent;
    vk.overlaysContent = true;
    const onGeom = () => emit(((vk.boundingRect && vk.boundingRect.height) || 0) > 0);
    vk.addEventListener('geometrychange', onGeom);
    open = ((vk.boundingRect && vk.boundingRect.height) || 0) > 0; // seed, do not fire
    return {
      live: true,
      stop: () => {
        vk.removeEventListener('geometrychange', onGeom);
        vk.overlaysContent = wasOverlaying;
      },
    };
  }

  const vv = window.visualViewport;
  if (vv) {
    // 120px, not a hair: an iOS URL-bar collapse is ~50px and is emphatically not a keyboard.
    const baseline = vv.height;
    const onResize = () => emit(vv.height < baseline - 120);
    vv.addEventListener('resize', onResize);
    open = false;
    return { live: true, stop: () => vv.removeEventListener('resize', onResize) };
  }
  return { stop: () => {}, live: false };
}

const WIDGET_RE = /,?\s*interactive-widget=[a-z-]+/;

/** Rewrite the live viewport meta's interactive-widget, returning the old value. */
function setInteractiveWidget(mode) {
  const meta = document.querySelector('meta[name="viewport"]');
  if (!meta) return null;
  const before = meta.getAttribute('content') || '';
  const found = before.match(/interactive-widget=([a-z-]+)/);
  const next = before.replace(WIDGET_RE, '') + (mode ? `, interactive-widget=${mode}` : '');
  meta.setAttribute('content', next.replace(/^,\s*/, ''));
  return found ? found[1] : '';
}

function currentInset() {
  const vk = typeof navigator !== 'undefined' ? navigator.virtualKeyboard : null;
  if (vk && vk.boundingRect) {
    const kb = vk.boundingRect.height || 0;
    return { visibleHeight: Math.max(0, window.innerHeight - kb), offsetTop: 0, keyboardHeight: kb };
  }
  const vv = typeof window !== 'undefined' ? window.visualViewport : null;
  if (vv) {
    return {
      visibleHeight: Math.round(vv.height),
      offsetTop: Math.round(vv.offsetTop),
      keyboardHeight: Math.max(0, Math.round(window.innerHeight - vv.height)),
    };
  }
  return { visibleHeight: window.innerHeight, offsetTop: 0, keyboardHeight: 0 };
}

/**
 * ⭐ STOP MEASURING THE KEYBOARD. LET THE BROWSER DO IT.
 *
 * Three fixes tried to compute the keyboard's height and size a box to match, and
 * each failed differently, because the number is not knowable from here: the two
 * sensors disagree, one of them is secure-context only, and the keyboard's height
 * CHANGES UNDER YOU — Chrome adds an autofill strip (password / card / address) above
 * the keys when a field takes focus. That strip is almost certainly the 64px that
 * three rounds of arithmetic could not account for, and nothing we can query knew it
 * existed.
 *
 * `interactive-widget=resizes-content` makes the browser shrink the LAYOUT viewport
 * to whatever space genuinely remains, strip and all. `height: 100dvh` is then exact
 * by definition, with no measurement, no API, and no estimate to be wrong. Verified
 * on Ryan's OnePlus 15: the panel's bottom edge lands ON the keyboard.
 *
 * ⚠ SCOPED TO WHILE THE SURFACE IS OPEN, and that scoping is the whole reason this
 * is safe here. WD 253 moved the app to `overlays-content` precisely because a
 * shrinking layout viewport dragged the design sheet, the canvas and the camera fit
 * around. That still applies — but a full-screen help panel COVERS the canvas, so
 * nothing anyone can see moves while this is in force. Restored on unsubscribe to
 * whatever main.js set, never hard-coded back.
 *
 * @param {(state: {keyboardOpen: boolean}) => void} onChange
 * @returns {() => void} unsubscribe
 */
export function observeKeyboard(onChange) {
  if (typeof window === 'undefined') return () => {};

  const previous = setInteractiveWidget('resizes-content');
  // Captured AFTER the swap: the keyword change can itself reflow, and a baseline
  // taken before it would read every later comparison as a keyboard.
  const baseline = window.innerHeight;
  const emit = () => onChange({ keyboardOpen: window.innerHeight < baseline - 80 });

  window.addEventListener('resize', emit);
  emit();
  return () => {
    window.removeEventListener('resize', emit);
    if (previous !== null) setInteractiveWidget(previous);
  };
}
